"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.videoAgentVoiceSchema = exports.systemAvatarSchema = exports.voiceOptionSchema = exports.platformKbOptionSchema = exports.customerServiceQuotaViewSchema = exports.customerServiceViewSchema = exports.listCustomerServiceQuerySchema = exports.updateCustomerServiceSchema = exports.createCustomerServiceSchema = exports.customerServiceI18nSchema = exports.TITLE_BG_GRADIENT_RE = exports.INVOKE_EVENT_NAME_RE = exports.OFFSET_MAX_PERCENT = exports.OFFSET_MAX_PX = exports.offsetUnitEnum = exports.buttonPositionEnum = exports.defaultWindowEnum = exports.customerServiceTypeEnum = void 0;
const zod_1 = require("zod");
exports.customerServiceTypeEnum = zod_1.z.enum(['web', 'digital']);
exports.defaultWindowEnum = zod_1.z.enum(['min', 'max']);
/** 迷你按钮在宿主网页中的水平贴边方向 */
exports.buttonPositionEnum = zod_1.z.enum(['left', 'right']);
/** 偏移单位（X/Y 共用）: px=像素, percent=百分比(水平按 vw、垂直按 vh) */
exports.offsetUnitEnum = zod_1.z.enum(['px', 'percent']);
/** 偏移上限: px 模式 0-512 像素; percent 模式 0-90(见 superRefine) */
exports.OFFSET_MAX_PX = 512;
exports.OFFSET_MAX_PERCENT = 90;
/**
 * 外部唤起 window 自定义事件名白名单。
 * 首字符字母,后续仅字母数字与 : _ - ,长度 ≤64(覆盖 dcs:widget:open 这类命名空间写法)。
 * 事件名不进 DOM / inline style(widget 仅作 addEventListener 字符串),
 * 白名单主要防脏数据 / 意外字符,与 TITLE_BG_GRADIENT_RE 同思路。
 */
exports.INVOKE_EVENT_NAME_RE = /^[a-zA-Z][a-zA-Z0-9:_-]{0,63}$/;
/**
 * 网页客服标题栏渐变背景的合法字符串模式。
 * 仅放行 `linear-gradient(<0-360>deg, #RRGGBB, #RRGGBB)` 这一种语法,
 * 防止管理员手填 / 接口注入任意 CSS,让 widget 端可以放心写到 inline style 上。
 */
exports.TITLE_BG_GRADIENT_RE = /^linear-gradient\(\s*\d{1,3}deg\s*,\s*#[0-9a-fA-F]{6}\s*,\s*#[0-9a-fA-F]{6}\s*\)$/;
/**
 * 客服多语言译文容器 (welcomeMsg / guideMsg / fallbackMsg / humanWelcomeMsg 等文案字段的统一 i18n 存放)
 * 结构: { "<locale>": { "<fieldName>": "<译文>" } }
 *   e.g. { "en": { "welcomeMsg": "Hi!", "humanWelcomeMsg": "Connecting you to an agent..." } }
 * 后续新增可翻译字段时,直接复用此 record,不必再加列。
 */
exports.customerServiceI18nSchema = zod_1.z
    .record(zod_1.z.string(), zod_1.z.record(zod_1.z.string(), zod_1.z.string()))
    .nullable()
    .optional();
/**
 * 写接口入参 (FE 上传完成后提交 *Key 字段;BE 持久化到 dcs_customer_services 同名 *_key 列)
 * spec §5.6 OBS 私有桶: avatar/wechat 二维码以 OBS object key 形式提交
 */
const customerServiceWriteSchema = zod_1.z.object({
    type: exports.customerServiceTypeEnum,
    name: zod_1.z.string().min(1).max(100),
    avatarKey: zod_1.z.string().max(512).nullable().optional(),
    // 选中系统形象时回传:自建库形象 UUID(历史直代理数据为第三方 id,BE 按 external_id 兜底归一)。
    // systemAvatarUrl 仅编辑期预览回传,BE 保存时归一置空(签名 URL 1h 过期不能存快照)
    systemAvatarId: zod_1.z.string().max(64).nullable().optional(),
    systemAvatarUrl: zod_1.z.string().url().max(1024).nullable().optional(),
    themeColor: zod_1.z.string().max(16).nullable().optional(),
    titleBgGradient: zod_1.z
        .string()
        .max(128)
        .regex(exports.TITLE_BG_GRADIENT_RE, '渐变格式必须为 linear-gradient(<角度>deg, #RRGGBB, #RRGGBB)')
        .nullable()
        .optional(),
    welcomeMsg: zod_1.z.string().max(1000).nullable().optional(),
    guideMsg: zod_1.z.string().max(1000).nullable().optional(),
    fallbackMsg: zod_1.z.string().max(1000).nullable().optional(),
    // 人工客服招呼语:转人工接通后 widget 主动展示;留空则前端回退内置文案
    humanWelcomeMsg: zod_1.z.string().max(1000).nullable().optional(),
    // 订阅的平台万云3知识库 ID 列表（knowledge-hub kb_relations.id，非 UUID，不透明字符串）；
    // 一客服可订阅多个，是 AI 知识库的唯一来源（旧单值本地知识库 cs.kbId 已彻底下线）。
    kbIds: zod_1.z.array(zod_1.z.string().min(1).max(64)).max(50).optional(),
    // video-agent 音色 id（如 voice_001,非 UUID）
    voiceId: zod_1.z.string().max(64).nullable().optional(),
    showButton: zod_1.z.boolean().optional(),
    defaultWindow: exports.defaultWindowEnum.optional(),
    // ── widget 位置 / 显示 / 外部唤起 ──
    buttonPosition: exports.buttonPositionEnum.optional(),
    offsetUnit: exports.offsetUnitEnum.optional(),
    offsetX: zod_1.z.number().int().min(0).max(exports.OFFSET_MAX_PX).optional(),
    offsetY: zod_1.z.number().int().min(0).max(exports.OFFSET_MAX_PX).optional(),
    invokeEventName: zod_1.z
        .string()
        .min(1)
        .max(64)
        .regex(exports.INVOKE_EVENT_NAME_RE, '事件名须字母开头,仅含字母数字及 : _ - ,长度≤64')
        .optional(),
    phone: zod_1.z.string().max(32).nullable().optional(),
    wechatQrKey: zod_1.z.string().max(512).nullable().optional(),
    workTime: zod_1.z.string().max(128).nullable().optional(),
    i18n: exports.customerServiceI18nSchema,
    // 绑定的主站站点 ID 列表（来自主站 /api/v1/sites 的 id）；一个客服可绑定多个站点
    siteIds: zod_1.z.array(zod_1.z.string().uuid()).max(200).optional(),
});
/**
 * 跨字段校验:percent 单位下偏移上限收紧为 90(px 单位沿用字段级 max 512)。
 * 字段可能 undefined(create 未传 / update partial),仅在确为 percent 且数值越界时报错,
 * 故同一函数可复用到 create 与 update 两个 schema。
 */
const refineOffsetPercent = (val, ctx) => {
    if (val.offsetUnit !== 'percent')
        return;
    for (const key of ['offsetX', 'offsetY']) {
        const v = val[key];
        if (typeof v === 'number' && v > exports.OFFSET_MAX_PERCENT) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: `percent 模式下偏移不能超过 ${exports.OFFSET_MAX_PERCENT}`,
            });
        }
    }
};
/**
 * 写接口入参 (FE 上传完成后提交 *Key 字段)。
 * 注:带 superRefine → 类型为 ZodEffects,不能再 .partial()/.extend(),
 * 故 update 直接基于内部 customerServiceWriteSchema 复刻。
 */
exports.createCustomerServiceSchema = customerServiceWriteSchema.superRefine(refineOffsetPercent);
/**
 * 更新接口入参
 * - 继承 create 字段全部 partial
 * - 额外允许 `status: 'active' | 'inactive'`(用于列表页启停 Switch)
 */
exports.updateCustomerServiceSchema = customerServiceWriteSchema
    .partial()
    .extend({
    status: zod_1.z.enum(['active', 'inactive']).optional(),
})
    .superRefine(refineOffsetPercent);
exports.listCustomerServiceQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(100).default(20),
    type: exports.customerServiceTypeEnum.optional(),
    keyword: zod_1.z.string().max(64).optional(),
    // 状态过滤(open/v1 管理 API 需要;admin 列表页不传即全部,向后兼容)
    status: zod_1.z.enum(['active', 'inactive']).optional(),
});
/**
 * 出参 View (BE 投影后 + 签名 URL)
 * 写表单时 FE 用 *Key 提交;读列表时 FE 用 *Url 渲染
 */
exports.customerServiceViewSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    siteId: zod_1.z.string().uuid(),
    merchantId: zod_1.z.string().uuid(),
    type: exports.customerServiceTypeEnum,
    clientId: zod_1.z.string().uuid(),
    name: zod_1.z.string(),
    avatarUrl: zod_1.z.string().nullable(), // 形象图 OBS 签名 GET URL(1h);系统形象与自定义统一走 avatar_key 签名
    avatarKey: zod_1.z.string().nullable(), // 形象图 OBS key（系统形象=库图 key,自定义=上传 key）,FE 回显用
    avatarSource: zod_1.z.enum(['system', 'custom']).nullable(), // 形象来源: system=系统形象(第三方), custom=自定义上传, null=未设置
    systemAvatarId: zod_1.z.string().nullable(), // 选中的第三方系统形象 id,avatarSource=system 时非空
    systemAvatarUrl: zod_1.z.string().nullable(), // 第三方系统形象图片 URL(直接展示)
    themeColor: zod_1.z.string().nullable(),
    titleBgGradient: zod_1.z.string().nullable(),
    welcomeMsg: zod_1.z.string().nullable(),
    guideMsg: zod_1.z.string().nullable(),
    fallbackMsg: zod_1.z.string().nullable(),
    // 人工客服招呼语:转人工接通后 widget 主动展示
    humanWelcomeMsg: zod_1.z.string().nullable(),
    // 已订阅的平台知识库 ID 列表（回显多选；来源本地 customer_service_kbs 活跃行）
    kbIds: zod_1.z.array(zod_1.z.string()).default([]),
    // 已订阅 KB 的「id→名称」映射（回显 label 兜底；候选列表未覆盖历史 id 时用本映射显示名）
    kbNames: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).default({}),
    voiceId: zod_1.z.string().nullable(), // video-agent 音色 id（如 voice_001）
    // 数字人形象生成视频（自定义形象异步生成）
    avatarVideoStatus: zod_1.z.enum(['none', 'pending', 'generating', 'ready', 'failed']), // 决定数字人客服是否可用
    avatarVideoUrl: zod_1.z.string().nullable(), // 生成视频 OBS 签名 URL；status=ready 时非空
    avatarVideoDurationMs: zod_1.z.number().int().nullable(), // 生成视频总时长(ms)，widget 切片用
    showButton: zod_1.z.boolean(),
    defaultWindow: exports.defaultWindowEnum,
    // ── widget 位置 / 显示 / 外部唤起（BE toView 永远给值，故非 nullable）──
    buttonPosition: exports.buttonPositionEnum,
    offsetUnit: exports.offsetUnitEnum,
    offsetX: zod_1.z.number().int(),
    offsetY: zod_1.z.number().int(),
    invokeEventName: zod_1.z.string(),
    phone: zod_1.z.string().nullable(),
    wechatQrUrl: zod_1.z.string().nullable(),
    wechatQrKey: zod_1.z.string().nullable(),
    workTime: zod_1.z.string().nullable(),
    status: zod_1.z.string(),
    i18n: exports.customerServiceI18nSchema,
    // 已绑定的主站站点 ID 列表（回显多选下拉，反查接口与编辑表单共用）
    siteIds: zod_1.z.array(zod_1.z.string()).default([]),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
/**
 * 客服创建限额预检视图 (GET /admin/customer-services/quota?type= 返回)。
 * FE 点「新建客服」选具体类型时调用：allowed=false 则 toast(message) 且不进入创建表单。
 *  - limit: null=不限制；0=该类型禁用；正整数=上限
 *  - used:  当前商户该类型已建客服数（软删除已排除）
 *  - message: allowed=false 时的友好提示文案（与后端兜底拦截抛出的提示一致）
 */
exports.customerServiceQuotaViewSchema = zod_1.z.object({
    type: exports.customerServiceTypeEnum,
    limit: zod_1.z.number().int().nullable(),
    used: zod_1.z.number().int(),
    unit: zod_1.z.string(),
    allowed: zod_1.z.boolean(),
    message: zod_1.z.string().optional(),
});
/**
 * 平台知识库候选项 (GET /admin/kb-hub/options 数据源，客服编辑页多选下拉用)。
 * 平台 knowledge-hub 列表响应字段不稳定，BE (KbHubClient.listKbs) 兼容解析后裁剪为
 * { id, name } 回传：id=平台 kbId(不透明字符串，非 UUID)，name=知识库名称。
 */
exports.platformKbOptionSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    name: zod_1.z.string(),
});
/**
 * @deprecated 本地音色字典 (digital_cs_voice_options)。数字人客服表单已改为从
 * video-agent 拉取系统音色 (videoAgentVoiceSchema),此 schema 仅为向后兼容保留,
 * 新代码勿用。
 *
 * - 来源: 豆包 TTS / 火山引擎 TTS 服务商内置音色目录
 * - voiceId 字段在 customerServiceViewSchema 现已放宽为 string (存 video-agent 音色 id)
 */
exports.voiceOptionSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    vendor: zod_1.z.enum(['doubao', 'volc']),
    voiceId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    gender: zod_1.z.enum(['male', 'female', 'neutral']),
    locale: zod_1.z.string(),
});
/**
 * 系统数字人形象 (GET /admin/digital-human/system-avatars 列表项)
 * - 当前实现: 查自建库 digital_cs_system_avatars(SystemAvatarService,数据由
 *   seed-local-system-avatars 预置,口播视频已提前生成),thumbnailUrl 为本项目
 *   OBS 私有桶实时签名 URL(1h)。
 * - admin 弹窗挑选;选中后把本地行 UUID 存 systemAvatarId(BE 保存时归一
 *   avatarKey=imageKey、systemAvatarUrl 置空)。
 * - gender 用宽松 string;previewVideoUrl 可能空串,不加 .url() 约束。
 */
exports.systemAvatarSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    gender: zod_1.z.string(),
    thumbnailUrl: zod_1.z.string(),
    imageKey: zod_1.z.string().optional(),
    previewVideoUrl: zod_1.z.string().optional(),
});
/**
 * video-agent 系统音色 (GET /kma/gpt/video-agent/voices 列表项)
 * - BE 代理 GET /admin/digital-human/voices 返回
 * - 取代本地 voiceOptionSchema 作为数字人客服音色下拉数据源
 * - 选中后把 id 存进客服记录 voiceId
 */
exports.videoAgentVoiceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    description: zod_1.z.string().optional(),
    gender: zod_1.z.string(),
    source: zod_1.z.string(),
    previewUrl: zod_1.z.string().optional(),
});
//# sourceMappingURL=customer-service.js.map